import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DiagramInfo } from "@/types/diagram";
import { AlertCircle } from "lucide-react";

type Mode = "save" | "clone" | "edit_details" | "delete";

interface SaveNewDiagramProps {
  mode: Mode;
  isOpen: boolean;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (name: string, description: string) => void;
  diagramInfo: DiagramInfo | null;
  error?: string | null;
  onErrorClear?: () => void;
}

const SaveNewDiagram: React.FC<SaveNewDiagramProps> = ({
  mode,
  isOpen,
  isPending,
  onClose,
  onSubmit,
  diagramInfo,
  error,
  onErrorClear,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (isOpen && diagramInfo) {
      setName(diagramInfo.name || "");
      setDescription(diagramInfo.metadata?.description || "");
    } else if (isOpen && mode === "save") {
      setName("");
      setDescription("");
    }
  }, [isOpen, diagramInfo, mode]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    // Clear error when user starts typing a new name
    if (error && onErrorClear) {
      onErrorClear();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(name, description);
  };

  const getTitle = () => {
    switch (mode) {
      case "save":
        return "Save New Diagram";
      case "clone":
        return "Clone Diagram";
      case "edit_details":
        return "Edit Diagram Details";
      case "delete":
        return "Delete Diagram";
      default:
        return "Diagram";
    }
  };

  const getDescription = () => {
    switch (mode) {
      case "save":
        return "Enter a name and description for your new diagram";
      case "clone":
        return "Enter details for the cloned diagram";
      case "edit_details":
        return "Update the diagram details";
      case "delete":
        return `Are you sure you want to delete "${diagramInfo?.name}"? This action cannot be undone.`;
      default:
        return "";
    }
  };

  if (mode === "delete") {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-surface border-surface-border text-white">
          <DialogHeader>
            <DialogTitle className="text-white">{getTitle()}</DialogTitle>
            <DialogDescription className="text-muted">
              {getDescription()}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isPending}
              className="bg-surface border border-surface-border hover:bg-surface-hover hover:border-surface-border text-white"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => onSubmit("", "")}
              disabled={isPending}
              className="bg-error hover:bg-error/90 text-white"
            >
              {isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-surface border-surface-border text-white">
        <DialogHeader>
          <DialogTitle className="text-white">{getTitle()}</DialogTitle>
          <DialogDescription className="text-muted">
            {getDescription()}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-error/10 border border-error rounded-md">
              <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}
          <div>
            <Label htmlFor="name" className="text-white">
              Name
            </Label>
            <Input
              id="name"
              value={name}
              onChange={handleNameChange}
              className="bg-surface-active text-white placeholder:text-muted"
              placeholder="Enter diagram name"
              required
            />
          </div>
          <div>
            <Label htmlFor="description" className="text-white">
              Description
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-surface-active text-white placeholder:text-muted"
              placeholder="Enter description (optional)"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isPending}
              className="bg-surface border border-surface-border hover:bg-surface-hover hover:border-surface-border text-white"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isPending || !name.trim()}
              className="bg-primary hover:bg-primary-dark text-white"
            >
              {isPending
                ? "Saving..."
                : mode === "save"
                ? "Save"
                : mode === "clone"
                ? "Clone"
                : "Update"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SaveNewDiagram;